import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAuthStore } from './authStore';
import { completeOnboardingStepByName } from '@lib/services/integration/integrationApi';
import type { UploadedFile } from '../types/onboarding';
import { 
  SHOPIFY_ONBOARDING_STEPS, 
  NON_SHOPIFY_ONBOARDING_STEPS,
  type OnboardingStepDefinition
} from '@constants/userFlow';

// Utility functions for step name management
const parseStepNames = (stepName?: string): string[] => {
  if (!stepName || stepName.trim() === '') {
    return [];
  }
  return stepName
    .split(',')
    .map(name => name.trim())
    .filter(Boolean);
};

const _stringifyStepNames = (stepNames: string[]): string => {
  return stepNames.filter(Boolean).join(',');
};

const isStepCompleted = (completedSteps: string[], stepId: string): boolean => {
  return completedSteps.includes(stepId);
};

// Re-export the step type for backward compatibility
type OnboardingStep = OnboardingStepDefinition;

interface OnboardingState {
  // Real user data
  completedStepNames: string[]; // From user.stepName parsed as array
  maxSteps: number; // Always 5 based on Angular
  isShopifyUser: boolean; // Based on user installations
  steps: OnboardingStep[];
  isLoading: boolean;
  error: string | null;
}

interface OnboardingStore extends OnboardingState {
  // Actions
  initializeFromUser: () => void;
  recordStepByName: (stepId: string) => Promise<void>;
  canAccessStepByName: (stepId: string) => boolean;
  getStepStatusByName: (stepId: string) => 'locked' | 'current' | 'completed';
  refreshFromUser: () => void;
  reset: () => void;

  // File management
  uploadedFiles: UploadedFile[];
  addUploadedFile: (file: UploadedFile) => void;
  removeUploadedFile: (fileId: string) => void;

  // Step navigation
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  canProceedToNext: boolean;
  canGoToPrevious: boolean;
  skipStep: () => void;
  completeStep: () => void;
}

// Step definitions based on Angular implementation
const createStepDefinitions = (isShopifyUser: boolean): OnboardingStep[] => {
  const baseSteps = isShopifyUser ? SHOPIFY_ONBOARDING_STEPS : NON_SHOPIFY_ONBOARDING_STEPS;
  
  // Add 'locked' status to all steps initially
  return baseSteps.map(step => ({
    ...step,
    status: 'locked' as const,
  }));
};

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set, get) => ({
      // Initial state
      completedStepNames: [],
      maxSteps: 5,
      isShopifyUser: false,
      steps: [],
      isLoading: false,
      error: null,

      // Initialize from real user data
      initializeFromUser: () => {
        const user = useAuthStore.getState().user;
        if (!user) {
          console.warn('No user found, cannot initialize onboarding');
          return;
        }

        // Check if user has Shopify installation
        const hasShopifyInstallation =
          user.installations?.some(
            installation =>
              installation.platform === 'shopify' && installation.status !== 'uninstalled'
          ) || false;

        // Create step definitions first
        const stepDefinitions = createStepDefinitions(hasShopifyInstallation);

        // Parse completed step names from user.stepName
        const allCompletedSteps = parseStepNames(user.stepName);

        // Combine mapped steps with any existing valid onboarding steps
        const validStepIds = stepDefinitions.map(step => step.id);
        const existingValidSteps = allCompletedSteps.filter(stepName =>
          validStepIds.includes(stepName)
        );

        // Remove duplicates and create final completed steps list
        const completedSteps = [...new Set([...existingValidSteps])];

        console.log('📊 Initializing onboarding from user:', {
          userId: user.id,
          stepName: user.stepName,
          allCompletedSteps,
          existingValidSteps,
          finalCompletedSteps: completedSteps,
          hasShopifyInstallation,
          validStepIds,
        });

        // Set step statuses based on user's actual progress (using step names)
        const getStatusByName = (stepId: string): 'locked' | 'current' | 'completed' => {
          // Check if step is completed by step name
          if (isStepCompleted(completedSteps, stepId)) {
            return 'completed';
          }

          // Find the next step that should be current
          const allStepIds = stepDefinitions.map(s => s.id);
          const nextIncompleteIndex = allStepIds.findIndex(
            id => !isStepCompleted(completedSteps, id)
          );

          if (nextIncompleteIndex !== -1 && allStepIds[nextIncompleteIndex] === stepId) {
            return 'current';
          }

          return 'locked';
        };

        const stepsWithStatus = stepDefinitions.map(step => ({
          ...step,
          status: getStatusByName(step.id),
        }));

        set({
          completedStepNames: completedSteps,
          maxSteps: stepDefinitions.length, // Ensure maxSteps is set correctly
          isShopifyUser: hasShopifyInstallation,
          steps: stepsWithStatus,
          isLoading: false,
          error: null,
        });

        console.log('Onboarding initialized:', {
          completedSteps: completedSteps.length,
          maxSteps: stepDefinitions.length,
          isShopifyUser: hasShopifyInstallation,
          stepsCompleted: `${completedSteps.length}/${stepDefinitions.length}`,
        });
      },

      // Record step completion by step name (new method)
      recordStepByName: async (stepId: string) => {
        const { completedStepNames } = get();
        const user = useAuthStore.getState().user;

        if (!user?.id) {
          set({ error: 'User not authenticated' });
          return;
        }

        // Check if step is already completed (in UI steps)
        if (isStepCompleted(completedStepNames, stepId)) {
          console.log(`Step '${stepId}' is already completed`);
          return;
        }

        set({ isLoading: true, error: null });

        try {
          console.log(`🗺️ Completing step by name: ${stepId}`);
          console.log(`📝 Existing UI steps: ${completedStepNames.join(', ')}`);

          // Use the user's complete stepName, not just the UI steps from store
          const userCompleteSteps = user.stepName ? parseStepNames(user.stepName) : [];
          console.log(`📝 User's complete stepName: ${userCompleteSteps.join(', ')}`);

          // Call new API to record step by name with user's complete existing steps
          const updatedUser = await completeOnboardingStepByName(
            user.id,
            stepId,
            userCompleteSteps
          );

          // Parse updated step names
          const newCompletedSteps = parseStepNames(updatedUser.stepName);

          // Update local state with server response
          set({
            completedStepNames: newCompletedSteps,
            isLoading: false,
          });

          // Refresh step statuses
          get().refreshFromUser();

          console.log(
            `✅ Step '${stepId}' completed successfully. New stepName: ${updatedUser.stepName}`
          );
        } catch (error) {
          console.error('Failed to record onboarding step by name:', error);
          set({
            isLoading: false,
            error: `Failed to record step '${stepId}'. Please try again.`,
          });
        }
      },

      // Check if user can access a specific step by name
      canAccessStepByName: (stepId: string) => {
        const { completedStepNames, steps } = get();

        // If step is completed, user can always access it
        if (isStepCompleted(completedStepNames, stepId)) {
          return true;
        }

        // Find all step IDs in order
        const allStepIds = steps.map(s => s.id);
        const stepIndex = allStepIds.indexOf(stepId);

        if (stepIndex === -1) {
          return false;
        }

        // Check if all previous steps are completed
        const previousStepIds = allStepIds.slice(0, stepIndex);
        const allPreviousCompleted = previousStepIds.every(id =>
          isStepCompleted(completedStepNames, id)
        );

        return allPreviousCompleted;
      },

      // Get step status by step name
      getStepStatusByName: (stepId: string) => {
        const { completedStepNames, steps: _steps } = get();

        // If step is completed
        if (isStepCompleted(completedStepNames, stepId)) {
          return 'completed';
        }

        // If step is not accessible, it's locked
        if (!get().canAccessStepByName(stepId)) {
          return 'locked';
        }

        // If accessible but not completed, it's current
        return 'current';
      },

      // Refresh state from current user data
      refreshFromUser: () => {
        const { steps, completedStepNames: currentCompletedSteps } = get();
        const user = useAuthStore.getState().user;

        if (!user) {
          console.warn('🚫 Cannot refresh onboarding: no user found');
          return;
        }

        // Parse all completed steps
        const allCompletedSteps = parseStepNames(user.stepName);

        // Combine mapped steps with any existing valid onboarding steps
        const validStepIds = steps.map(step => step.id);
        const existingValidSteps = allCompletedSteps.filter(stepName =>
          validStepIds.includes(stepName)
        );

        const completedSteps = [...new Set([...existingValidSteps])];

        console.log('🔄 Refreshing onboarding from user:', {
          userId: user.id,
          stepName: user.stepName,
          allCompletedSteps,
          existingValidSteps,
          finalCompletedSteps: completedSteps,
          currentCompletedSteps,
          hasChanged: JSON.stringify(completedSteps) !== JSON.stringify(currentCompletedSteps),
        });

        // Helper function to determine step status using step names
        const getStatusByName = (stepId: string): 'locked' | 'current' | 'completed' => {
          // Check if step is completed by step name
          if (isStepCompleted(completedSteps, stepId)) {
            return 'completed';
          }

          // Find the next step that should be current
          const allStepIds = steps.map(s => s.id);
          const nextIncompleteIndex = allStepIds.findIndex(
            id => !isStepCompleted(completedSteps, id)
          );

          if (nextIncompleteIndex !== -1 && allStepIds[nextIncompleteIndex] === stepId) {
            return 'current';
          }

          return 'locked';
        };

        // Update step statuses using step names
        const updatedSteps = steps.map(step => ({
          ...step,
          status: getStatusByName(step.id),
        }));

        set({
          completedStepNames: completedSteps,
          steps: updatedSteps,
        });
      },

      // Reset onboarding state
      reset: () => {
        set({
          completedStepNames: [],
          maxSteps: 5,
          isShopifyUser: false,
          steps: [],
          isLoading: false,
          error: null,
        });
      },

      // File management
      uploadedFiles: [],
      addUploadedFile: (file: UploadedFile) => {
        set(state => ({
          uploadedFiles: [...state.uploadedFiles, file],
        }));
      },
      removeUploadedFile: (fileId: string) => {
        set(state => ({
          uploadedFiles: state.uploadedFiles.filter(file => file.id !== fileId),
        }));
      },

      // Step navigation (placeholder methods for UI compatibility)
      goToNextStep: () => {
        // These methods are deprecated in favor of recordStepByName
        console.warn('goToNextStep is deprecated, use recordStepByName instead');
      },
      goToPreviousStep: () => {
        // These methods are deprecated in favor of recordStepByName
        console.warn('goToPreviousStep is deprecated, use recordStepByName instead');
      },
      canProceedToNext: true,
      canGoToPrevious: true,
      skipStep: () => {
        // These methods are deprecated in favor of recordStepByName
        console.warn('skipStep is deprecated, use recordStepByName instead');
      },
      completeStep: () => {
        // These methods are deprecated in favor of recordStepByName
        console.warn('completeStep is deprecated, use recordStepByName instead');
      },
    }),
    {
      name: 'onboarding-store',
      // Only persist non-computed state
      partialize: state => ({
        completedStepNames: state.completedStepNames,
        isShopifyUser: state.isShopifyUser,
      }),
    }
  )
);
